import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';

import * as UserSettingsActions from './+state/user-settings/user-settings.actions';
import * as UserSettingsSelectors from './+state/user-settings/user-settings.selectors';
import { filter, Observable, skipUntil } from 'rxjs';
import { UserSettingService } from './user-setting.service';
import { ActivatedRoute } from '@angular/router';
import { ShareRouterPersistencePayload } from './models/user-settings.models';
import { isEmpty, isNil } from 'lodash';
import {
  SAVE_TO_CITY_VIEW_CONFIG,
  SAVE_TO_MODEL_CONFIG,
  SAVE_TO_RENEWABLES_CONFIG,
  SAVE_TO_SEASONALS_CONFIG,
  SAVE_TO_SUMMARY_VIEW_CONFIG,
} from './constants';
import { IBlurbFilter } from '../../../news/src/lib/interfaces/blurb.interface';
import {
  RenewablesChartStylesSettings,
  WeightedChartStylesSettings,
} from './types/user-settings.types';
import { IBlogFilter } from '../../../news/src/lib/interfaces/blog.interface';

@Injectable({
  providedIn: 'root',
})
export class UserSettingsFacade {
  loaded$ = this.store.pipe(
    select(UserSettingsSelectors.getUserSettingsLoaded)
  );
  allUserUnits$ = this.store.pipe(
    select(UserSettingsSelectors.getAllUnits),
    this.skipWhenLoading()
  );
  selectedPrecipUnit$ = this.store.pipe(
    select(UserSettingsSelectors.getPrecipUnit),
    this.skipWhenLoading()
  );

  selectedTempUnit$ = this.store.pipe(
    select(UserSettingsSelectors.getTempUnit),
    this.skipWhenLoading()
  );

  selectedWindUnit$ = this.store.pipe(
    select(UserSettingsSelectors.getWindUnit),
    this.skipWhenLoading()
  );

  selectedResponsiveness$ = this.store.pipe(
    select(UserSettingsSelectors.getResponsiveness),
    this.skipWhenLoading()
  );

  constructor(
    private readonly store: Store,
    private readonly userSettingsService: UserSettingService
  ) {}
  public init() {
    this.store.dispatch(UserSettingsActions.initUserSettings());
  }

  public changeTempUnit(tempUnit: string) {
    this.store.dispatch(UserSettingsActions.changeTempUnit({ tempUnit }));
  }

  public changePrecipUnit(precipUnit: string) {
    this.store.dispatch(UserSettingsActions.changePrecipUnit({ precipUnit }));
  }

  public changeWindUnit(windUnit: string) {
    this.store.dispatch(UserSettingsActions.changeWindUnit({ windUnit }));
  }

  public changeResponsiveness(responsiveness: boolean) {
    this.store.dispatch(
      UserSettingsActions.changeResponsiveness({ responsiveness })
    );
  }

  public getRouterPersistenceData(goUpBy?: number) {
    return this.userSettingsService.getRouterPersistenceData(goUpBy);
  }

  public getPersistenceDataByRoute(urlSegments: string[]) {
    return this.userSettingsService.getPersistenceDataByRoute(urlSegments);
  }

  public saveDataOnRouteNavigationStart(goUpBy?: number) {
    return this.userSettingsService.saveDataOnRouteNavigationStart(goUpBy);
  }

  public getCurrentLocationUrl(): string[] {
    return this.userSettingsService.getCurrentLocationUrlArray();
  }

  public setHomepage(urlSegments: string[]) {
    this.store.dispatch(UserSettingsActions.setHomepage({ path: urlSegments }));
  }

  public setNewsFeedBlurbFilter(filter: IBlurbFilter | null): void {
    this.store.dispatch(UserSettingsActions.setNewsFeedBlurbFilter({ filter }));
  }

  public setNewsFeedBlogFilter(filter: IBlogFilter | null): void {
    this.store.dispatch(UserSettingsActions.setNewsFeedBlogFilter({ filter }));
  }

  public setHomepageNthLevel(
    dedicatedRoutes: string[],
    urlSegment: string,
    level: number
  ) {
    this.store.dispatch(
      UserSettingsActions.setHomepageNthLevel({
        dedicatedRoutes,
        path: urlSegment,
        level,
      })
    );
  }

  public getHomepage() {
    return this.store
      .select(UserSettingsSelectors.getHomePage)
      .pipe(
        filter((urlSegments): urlSegments is string[] => urlSegments !== null)
      );
  }

  public getNewsFeedBlurbFilter(): Observable<IBlurbFilter | null | undefined> {
    return this.store.select(UserSettingsSelectors.getNewsFeedBlurbFilter);
  }

  public getNewsFeedBlogFilter(): Observable<IBlogFilter | null | undefined> {
    return this.store.select(UserSettingsSelectors.getNewsFeedBlogFilter);
  }

  public setWeightedChartStyles(
    styles: WeightedChartStylesSettings | null
  ): void {
    this.store.dispatch(UserSettingsActions.setWeightedChartStyles({ styles }));
  }

  public getWeightedChartStyles(): Observable<
    WeightedChartStylesSettings | null | undefined
  > {
    return this.store.select(UserSettingsSelectors.getWeightedChartStyles);
  }

  public setRenewablesChartStyles(
    styles: RenewablesChartStylesSettings | null
  ): void {
    this.store.dispatch(
      UserSettingsActions.setRenewablesChartStyles({ styles })
    );
  }

  public getRenewablesChartStyles(): Observable<
    RenewablesChartStylesSettings | null | undefined
  > {
    return this.store.select(UserSettingsSelectors.getRenewablesChartStyles);
  }

  public setForecastCommentarySecondaryLens(
    secondaryLens: string | null
  ): void {
    this.store.dispatch(
      UserSettingsActions.setForecastCommentarySecondaryLens({ secondaryLens })
    );
  }

  public getForecastCommentarySecondaryLens(): Observable<
    string | null | undefined
  > {
    return this.store.select(
      UserSettingsSelectors.getForecastCommentarySecondaryLens
    );
  }

  public navigateToPersistedSubRoute(
    activatedRoute: ActivatedRoute,
    redirectData?: any
  ) {
    return this.userSettingsService.navigateToPersistedSubRoute(
      activatedRoute,
      redirectData
    );
  }

  public shareRouterPersistence(payload: ShareRouterPersistencePayload): void {
    this.store.dispatch(UserSettingsActions.shareRouterPersistence(payload));
  }

  public saveState(path: string, value: any, clearPrevState = false) {
    this.userSettingsService.saveState(path, value, clearPrevState);
  }

  public getOneLevelUpRouterPersistenceData<D>() {
    return this.getRouterPersistenceData(1).pipe(
      filter((data): data is D => !isNil(data))
    );
  }

  public saveRegionDataToCityState(regionData: Record<string, string>) {
    this.saveDataToSpecificState(SAVE_TO_CITY_VIEW_CONFIG, regionData);
  }

  public saveRegionDataToSummaryState(regionData: Record<string, string>) {
    this.saveDataToSpecificState(SAVE_TO_SUMMARY_VIEW_CONFIG, regionData);
  }

  public saveRegionDataToSeasonalsState(regionData: Record<string, string>) {
    this.saveDataToSpecificState(SAVE_TO_SEASONALS_CONFIG, regionData);
  }

  public saveDataToModelState(data: Record<string, string>) {
    this.saveDataToSpecificState(SAVE_TO_MODEL_CONFIG, data);
  }

  public saveDataToRenewablesState(data: Record<string, string>) {
    this.saveDataToSpecificState(SAVE_TO_RENEWABLES_CONFIG, data);
  }

  public saveCurrentRoute(): void {
    this.userSettingsService.saveRoute(this.getCurrentLocationUrl().join('/'));
  }

  private saveDataToSpecificState(
    stateConfig:
      | typeof SAVE_TO_CITY_VIEW_CONFIG
      | typeof SAVE_TO_SEASONALS_CONFIG,
    regionData: Record<string, string>
  ) {
    stateConfig.forEach((config) => {
      const data = config.fields.reduce((acc, field) => {
        if (!regionData[field]) {
          return acc;
        }
        return { ...acc, [field]: regionData[field] };
      }, {});
      if (!isEmpty(data)) {
        this.saveState(config.url, data);
      }
    });
  }

  private skipWhenLoading<T>(): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) =>
      source.pipe(
        skipUntil(this.store.pipe(select(UserSettingsSelectors.loaded)))
      );
  }
}
